import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Loader2, MoreVertical } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { HoverHintContent, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import type { MrpAmlEntry, MrpSkuHoverHintFields } from "@/lib/mrp";
import { formatMrpSkuHoverHintTitle } from "@/lib/mrp";
import { computeHorizontalMenuPanel, FLOATING_POPOVER_Z_INDEX, type AnchoredMenuPanelBox } from "@/lib/viewportAnchoredPopover";

import {
  AML_ACTIVE_COLUMN_HINT,
  AML_DEACTIVATE_ACTION_HINT,
  AML_STATUS_COLUMN_HINT,
  amlCpnDetailPath,
  groupAmlEntriesByCpn,
  formatAmlMpnAliasesLine,
} from "./amlEntryUtils";

const AML_PART_INFO_COLUMN_HINT =
  "Additional information about this manufacturer part number alternate — from approved_components.part_description. Not the CPN catalog description (hover the CPN for that).";

export type AmlEntriesTableProps = {
  rows: MrpAmlEntry[];
  groupedByCpn: boolean;
  skuHoverHints?: Record<string, MrpSkuHoverHintFields>;
  /** When true, the CPN cell links to SKU detail with the AML tab (only applies to the grouped header cell when grouped). */
  linkCpnToDetail: boolean;
  isLoading: boolean;
  emptyMessage: string | null;
  requestBusy: boolean;
  /** When false, row action menu is disabled (hub read-only). */
  hubWritesAllowed?: boolean;
  hubReadOnlyTitle?: string;
  onEditNotes: (row: MrpAmlEntry) => void;
  onRequestActivate: (row: MrpAmlEntry) => void;
  onRequestDeactivate: (row: MrpAmlEntry) => void;
  onRequestDelete: (row: MrpAmlEntry) => void;
};

export function AmlEntriesTable({
  rows,
  groupedByCpn,
  skuHoverHints = {},
  linkCpnToDetail,
  isLoading,
  emptyMessage,
  requestBusy,
  hubWritesAllowed = true,
  hubReadOnlyTitle,
  onEditNotes,
  onRequestActivate,
  onRequestDeactivate,
  onRequestDelete,
}: AmlEntriesTableProps) {
  const [openRowMenuId, setOpenRowMenuId] = useState<number | null>(null);
  const [rowMenuPos, setRowMenuPos] = useState<AnchoredMenuPanelBox | null>(null);
  const amlTableScrollRef = useRef<HTMLDivElement | null>(null);

  const closeRowMenu = useCallback(() => {
    setOpenRowMenuId(null);
    setRowMenuPos(null);
  }, []);

  const updateRowMenuPosition = useCallback(() => {
    if (openRowMenuId == null) return;
    const trigger = document.querySelector(
      `[data-aml-row-menu-anchor="${openRowMenuId}"]`,
    ) as HTMLButtonElement | null;
    if (!trigger) return;
    setRowMenuPos(
      computeHorizontalMenuPanel({
        anchorRect: trigger.getBoundingClientRect(),
        menuWidth: 13 * 16,
        horizontalAlign: "end",
        gap: 4,
        maxHeightCap: Math.min(480, Math.floor(window.innerHeight * 0.88)),
      }),
    );
  }, [openRowMenuId]);

  const groups = groupedByCpn ? groupAmlEntriesByCpn(rows) : rows.map((row) => ({ cpn: row.cpn, rows: [row] }));

  const amlRowMenuTarget =
    openRowMenuId != null ? rows.find((candidateRow) => candidateRow.id === openRowMenuId) : undefined;

  useLayoutEffect(() => {
    if (openRowMenuId == null) return;
    updateRowMenuPosition();
    const onScrollOrResize = (): void => {
      updateRowMenuPosition();
    };
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.visualViewport?.addEventListener("resize", onScrollOrResize);
    window.visualViewport?.addEventListener("scroll", onScrollOrResize);
    const scrollEl = amlTableScrollRef.current;
    scrollEl?.addEventListener("scroll", onScrollOrResize);
    return () => {
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.visualViewport?.removeEventListener("resize", onScrollOrResize);
      window.visualViewport?.removeEventListener("scroll", onScrollOrResize);
      scrollEl?.removeEventListener("scroll", onScrollOrResize);
    };
  }, [openRowMenuId, updateRowMenuPosition]);

  useEffect(() => {
    if (openRowMenuId == null) return;
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target as Element | null;
      if (target?.closest?.("[data-aml-row-menu-panel], [data-aml-row-menu-anchor]")) return;
      closeRowMenu();
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") closeRowMenu();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [openRowMenuId, closeRowMenu]);

  const colSpan = groupedByCpn ? 9 : 10;
  const readOnlyMessage =
    hubReadOnlyTitle ?? "Your role has read-only access to the MRP hub module.";

  const cpnHoverHint = (cpn: string): string => formatMrpSkuHoverHintTitle(cpn, skuHoverHints[cpn]);

  return (
    <>
      <div ref={amlTableScrollRef} className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
            <tr>
              <th className="px-3 py-2">CPN</th>
              <th className="px-3 py-2">Category</th>
              {groupedByCpn ? null : <th className="px-3 py-2">Description</th>}
              <th className="px-3 py-2">
                <HoverHintContent hoverHint={AML_PART_INFO_COLUMN_HINT}>
                  <span>Part info</span>
                </HoverHintContent>
              </th>
              <th className="px-3 py-2">Manufacturer</th>
              <th className="px-3 py-2">MPN</th>
              <th className="px-3 py-2">Notes</th>
              <th className="px-3 py-2">
                <HoverHintContent hoverHint={AML_STATUS_COLUMN_HINT}>
                  <span>Status</span>
                </HoverHintContent>
              </th>
              <th className="px-3 py-2">
                <HoverHintContent hoverHint={AML_ACTIVE_COLUMN_HINT}>
                  <span>Active</span>
                </HoverHintContent>
              </th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              <tr>
                <td className="px-3 py-6 text-slate-500" colSpan={colSpan}>
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    <span className="sr-only">Loading</span>
                  </span>
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td className="px-3 py-6 text-slate-500" colSpan={colSpan}>
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              groups.flatMap((group, groupIndex) =>
                group.rows.map((row, indexInGroup) => {
                  const groupStripe =
                    groupIndex % 2 === 0 ? "bg-sky-50/60 border-l-4 border-sky-500" : "bg-white border-l-4 border-slate-300";
                  const inactiveTint = row.active ? "" : " text-slate-500";
                  const groupSeparator =
                    groupIndex > 0 && indexInGroup === 0 ? "border-t-2 border-slate-300" : "";
                  return (
                  <tr
                    key={row.id}
                    className={`${groupStripe}${inactiveTint} ${groupSeparator}`}
                  >
                    {indexInGroup === 0 ? (
                      <td
                        className="align-top px-3 py-2 font-mono text-xs"
                        rowSpan={group.rows.length}
                      >
                        {linkCpnToDetail ? (
                          <HoverHintContent hoverHint={cpnHoverHint(group.cpn)} block>
                            <Link
                              className="text-sky-700 underline decoration-sky-300 underline-offset-2 hover:text-sky-900"
                              to={amlCpnDetailPath(group.cpn)}
                            >
                              {group.cpn}
                            </Link>
                          </HoverHintContent>
                        ) : (
                          <HoverHintContent hoverHint={cpnHoverHint(group.cpn)}>
                            <span className="cursor-help border-b border-dotted border-slate-400">{group.cpn}</span>
                          </HoverHintContent>
                        )}
                      </td>
                    ) : null}
                    <td className="whitespace-nowrap px-3 py-2 text-slate-700">
                      {indexInGroup > 0 ? (
                        <span className="mr-1 inline-block text-slate-400 select-none" aria-hidden>
                          ↳
                        </span>
                      ) : null}
                      {row.category ?? "—"}
                    </td>
                    {groupedByCpn ? null : (
                      <td className="max-w-md min-w-[12rem] whitespace-normal px-3 py-2 text-slate-700">
                        {row.description ?? "—"}
                      </td>
                    )}
                    <td className="max-w-md min-w-[10rem] whitespace-normal px-3 py-2 text-slate-700">
                      {row.description?.trim() ? row.description : "—"}
                    </td>
                    <td className="px-3 py-2">{row.manufacturer_name ?? "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {row.mpn ? row.mpn : "—"}
                      {formatAmlMpnAliasesLine(row.mpn_aliases) ? (
                        <span className="mt-1 block text-[11px] font-sans text-slate-500">
                          Aliases: {formatAmlMpnAliasesLine(row.mpn_aliases)}
                        </span>
                      ) : null}
                    </td>
                    <td className="max-w-md min-w-[10rem] whitespace-normal px-3 py-2 text-slate-700">
                      {row.notes?.trim() ? row.notes : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <HoverHintContent hoverHint={AML_STATUS_COLUMN_HINT}>
                        <span
                          className={
                            row.status === "approved"
                              ? "rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800"
                              : row.status === "deprecated"
                                ? "rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-900"
                                : "rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700"
                          }
                        >
                          {row.status}
                        </span>
                      </HoverHintContent>
                    </td>
                    <td className="px-3 py-2 text-slate-600">
                      <HoverHintContent hoverHint={AML_ACTIVE_COLUMN_HINT}>
                        <span className="cursor-help border-b border-dotted border-slate-400">
                          {row.active ? "Yes" : "No"}
                        </span>
                      </HoverHintContent>
                    </td>
                    <td className="px-3 py-2 text-right align-top">
                      <div className="flex justify-end">
                        <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={readOnlyMessage} layout="inline">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 shrink-0 p-0 text-slate-500 hover:text-slate-900"
                            data-aml-row-menu-anchor={row.id}
                            aria-label={`Actions for ${row.cpn}`}
                            aria-haspopup="menu"
                            aria-expanded={openRowMenuId === row.id}
                            disabled={requestBusy || !hubWritesAllowed}
                            onClick={() => {
                              if (openRowMenuId === row.id) {
                                closeRowMenu();
                                return;
                              }
                              setOpenRowMenuId(row.id);
                            }}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </ReadOnlyHoverLayer>
                      </div>
                    </td>
                  </tr>
                  );
                }),
              )
            )}
          </tbody>
        </table>
      </div>

      {amlRowMenuTarget && rowMenuPos
        ? createPortal(
            <ul
              data-aml-row-menu-panel
              role="menu"
              className="fixed overflow-y-auto rounded-md border border-slate-200 bg-white py-1 text-sm text-slate-800 shadow-lg"
              style={{
                top: rowMenuPos.top,
                left: rowMenuPos.left,
                width: rowMenuPos.width,
                maxHeight: rowMenuPos.maxHeight,
                zIndex: FLOATING_POPOVER_Z_INDEX,
              }}
            >
              <li>
                <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={readOnlyMessage} layout="block">
                  <button
                    type="button"
                    role="menuitem"
                    className="w-full px-3 py-2 text-left hover:bg-slate-100 disabled:opacity-50"
                    disabled={requestBusy || !hubWritesAllowed}
                    onClick={() => {
                      closeRowMenu();
                      onEditNotes(amlRowMenuTarget);
                    }}
                  >
                    Edit notes
                  </button>
                </ReadOnlyHoverLayer>
              </li>
              <li>
                <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={readOnlyMessage} layout="block">
                  <HoverHintContent hoverHint={AML_DEACTIVATE_ACTION_HINT} block>
                    <button
                      type="button"
                      role="menuitem"
                      className="w-full px-3 py-2 text-left hover:bg-slate-100 disabled:opacity-50"
                      disabled={requestBusy || !hubWritesAllowed}
                      onClick={() => {
                        closeRowMenu();
                        const confirmMessage = amlRowMenuTarget.active
                          ? `Submit a request to deactivate this AML line for ${amlRowMenuTarget.cpn}? This only hides it from the default list. Status stays approved until it is set to deprecated.`
                          : `Submit a request to activate this AML line for ${amlRowMenuTarget.cpn}? This only shows it on the default list.`;
                        if (window.confirm(confirmMessage)) {
                          if (amlRowMenuTarget.active) {
                            onRequestDeactivate(amlRowMenuTarget);
                          } else {
                            onRequestActivate(amlRowMenuTarget);
                          }
                        }
                      }}
                    >
                      {amlRowMenuTarget.active ? "Request deactivate" : "Request activate"}
                    </button>
                  </HoverHintContent>
                </ReadOnlyHoverLayer>
              </li>
              <li>
                <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={readOnlyMessage} layout="block">
                  <button
                    type="button"
                    role="menuitem"
                    className="w-full px-3 py-2 text-left text-red-700 hover:bg-red-50 disabled:opacity-50"
                    disabled={requestBusy || !hubWritesAllowed}
                    onClick={() => {
                      closeRowMenu();
                      if (
                        window.confirm(
                          `Submit a request to delete this AML line (${amlRowMenuTarget.cpn})? An approver must accept before it is removed.`,
                        )
                      ) {
                        onRequestDelete(amlRowMenuTarget);
                      }
                    }}
                  >
                    Request delete
                  </button>
                </ReadOnlyHoverLayer>
              </li>
            </ul>,
            document.body,
          )
        : null}
    </>
  );
}
